'use client';

import { useEffect } from 'react';
import { CLIENT_TIME_ZONE_COOKIE } from '@/lib/clientTimeZoneCookie';

/**
 * Publishes the browser's IANA timezone to a cookie so Server Components can
 * resolve a calendar day exactly as the browser would.
 *
 * Without it, a server render formats `YYYY-MM-DD` in the deployment's zone
 * (UTC on Vercel) and the browser formats it in the user's, so any query key
 * built from a date differs between the two and a server prefetch lands in an
 * entry the client never reads. See `lib/dateKeys.ts`.
 *
 * Renders nothing and runs once per mount. Rewriting on every mount is what
 * keeps it correct for a user who changes zone: the stale value survives only
 * until the next navigation.
 */
export function ClientTimeZone() {
  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return;

    // Same attributes as the sidebar's cookie (`components/ui/sidebar.tsx:94`):
    // path-wide, lax, and a year, since a zone changes rarely and a stale value
    // costs one missed prefetch rather than a wrong render.
    document.cookie = `${CLIENT_TIME_ZONE_COOKIE}=${encodeURIComponent(tz)}; path=/; max-age=${
      60 * 60 * 24 * 365
    }; ${process.env.NODE_ENV === 'production' ? 'secure; ' : ''}samesite=lax`;
  }, []);

  return null;
}
